/*
 * DeadlyScience order limits.
 *
 * Reads "Max per order" limits written by snippets/order-limit-data.liquid
 * (product metafield custom.max_per_order = most per order across all variants,
 *  variant metafield custom.max_per_order = most of that variant per order).
 *
 * - Caps add-to-cart and cart quantity changes so the cart never goes over a limit.
 *   If nothing more can be added, the add is refused with a normal cart error, which the
 *   theme shows in its usual place.
 * - Sets max on product quantity fields and shows a "Limit N per order" note.
 *
 * This runs in the storefront only; it can't stop orders made by bypassing the theme.
 */
(function () {
  'use strict';

  if (window.__dsOrderLimits) return;
  window.__dsOrderLimits = true;

  var nativeFetch = window.fetch.bind(window);
  var cartCache = null;

  function message(limit) {
    return 'Limit ' + limit + ' per order';
  }

  // ---------- Limits from the page ----------
  function readLimits() {
    var map = {};
    var nodes = document.querySelectorAll('script[data-ds-order-limit]');
    for (var i = 0; i < nodes.length; i++) {
      var data;
      try {
        data = JSON.parse(nodes[i].textContent);
      } catch (e) {
        continue;
      }
      for (var j = 0; j < data.variantIds.length; j++) {
        var id = String(data.variantIds[j]);
        var variantMax = data.variants && data.variants[id] != null ? data.variants[id] : null;
        map[id] = { product: String(data.product), productMax: data.max, variantMax: variantMax };
      }
    }
    return map;
  }

  // ---------- Cart ----------
  function cartUrl() {
    var root = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    return root + '.js';
  }

  function getCart() {
    return nativeFetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cartCache = cart; return cart; });
  }

  function totals(cart, skipKey) {
    var byVariant = {};
    var byProduct = {};
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (skipKey && item.key === skipKey) continue;
      var v = String(item.variant_id);
      var p = String(item.product_id);
      byVariant[v] = (byVariant[v] || 0) + item.quantity;
      byProduct[p] = (byProduct[p] || 0) + item.quantity;
    }
    return { variant: byVariant, product: byProduct };
  }

  // How many more of this variant can go in, and which limit applies.
  function room(info, variantId, t) {
    var best = Infinity;
    var limit = null;
    if (info.variantMax != null) {
      var left = info.variantMax - (t.variant[variantId] || 0);
      if (left < best) { best = left; limit = info.variantMax; }
    }
    if (info.productMax != null) {
      var leftP = info.productMax - (t.product[info.product] || 0);
      if (leftP < best) { best = leftP; limit = info.productMax; }
    }
    return { left: Math.max(0, best), limit: limit };
  }

  function refusal(limit) {
    var body = JSON.stringify({ status: 422, message: message(limit), description: message(limit) });
    return Promise.resolve(new Response(body, {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    }));
  }

  // ---------- Request bodies ----------
  function parseJson(body) {
    if (typeof body !== 'string') return null;
    try { return JSON.parse(body); } catch (e) { return null; }
  }

  function addItems(body) {
    // FormData: single id + quantity (the theme's product forms and quick add)
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (!body.get('id')) return null;
      return {
        items: [{ id: String(body.get('id')), quantity: parseInt(body.get('quantity') || '1', 10) || 1 }],
        write: function (items) { body.set('quantity', String(items[0].quantity)); return body; }
      };
    }
    var json = parseJson(body);
    if (!json) return null;
    if (json.items && json.items.length) {
      return {
        items: json.items.map(function (it) { return { id: String(it.id), quantity: parseInt(it.quantity || 1, 10) || 1 }; }),
        write: function (items) {
          json.items = json.items.map(function (it, i) { it.quantity = items[i].quantity; return it; })
            .filter(function (it) { return it.quantity > 0; });
          return JSON.stringify(json);
        }
      };
    }
    if (json.id) {
      return {
        items: [{ id: String(json.id), quantity: parseInt(json.quantity || 1, 10) || 1 }],
        write: function (items) { json.quantity = items[0].quantity; return JSON.stringify(json); }
      };
    }
    return null;
  }

  // ---------- Fetch wrapper ----------
  function pathOf(input) {
    try {
      return new URL(typeof input === 'string' ? input : input.url, window.location.href).pathname;
    } catch (e) {
      return '';
    }
  }

  function handleAdd(input, init) {
    var parsed = addItems(init.body);
    var limits = readLimits();
    if (!parsed) return null;
    var limited = parsed.items.some(function (it) { return limits[it.id]; });
    if (!limited) return null;

    return getCart().then(function (cart) {
      var t = totals(cart);
      var refusedLimit = null;
      parsed.items.forEach(function (it) {
        var info = limits[it.id];
        if (!info) return;
        var r = room(info, it.id, t);
        if (it.quantity > r.left) it.quantity = r.left;
        if (it.quantity === 0 && refusedLimit === null) refusedLimit = r.limit;
        // Count this item so later items in the same request see it.
        t.variant[it.id] = (t.variant[it.id] || 0) + it.quantity;
        t.product[info.product] = (t.product[info.product] || 0) + it.quantity;
      });

      var anyLeft = parsed.items.some(function (it) { return it.quantity > 0; });
      if (!anyLeft) return refusal(refusedLimit);

      var newInit = {};
      for (var k in init) newInit[k] = init[k];
      newInit.body = parsed.write(parsed.items);
      return nativeFetch(input, newInit);
    });
  }

  function handleChange(input, init) {
    var json = parseJson(init.body);
    if (!json || json.quantity == null) return null;
    var limits = readLimits();

    return getCart().then(function (cart) {
      var item = null;
      if (json.line) item = cart.items[json.line - 1];
      if (!item && json.id != null) {
        var id = String(json.id);
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === id || String(cart.items[i].variant_id) === id) { item = cart.items[i]; break; }
        }
      }
      var info = item && limits[String(item.variant_id)];
      if (info) {
        var r = room(info, String(item.variant_id), totals(cart, item.key));
        var wanted = parseInt(json.quantity, 10) || 0;
        if (wanted > r.left) {
          json.quantity = r.left;
          var newInit = {};
          for (var k in init) newInit[k] = init[k];
          newInit.body = JSON.stringify(json);
          return nativeFetch(input, newInit);
        }
      }
      return nativeFetch(input, init);
    });
  }

  window.fetch = function (input, init) {
    init = init || {};
    var method = (init.method || (input && input.method) || 'GET').toUpperCase();
    if (method === 'POST' && init.body) {
      var path = pathOf(input);
      var pending = null;
      if (/\/cart\/add(\.js)?$/.test(path)) pending = handleAdd(input, init);
      else if (/\/cart\/change(\.js)?$/.test(path)) pending = handleChange(input, init);
      if (pending) {
        return pending.then(function (response) { scheduleSync(true); return response; });
      }
    }
    return nativeFetch(input, init);
  };

  // ---------- Product forms: max quantity + note ----------
  function field(form, name) {
    var el = form.elements && form.elements.namedItem(name);
    if (el && !el.nodeType && el.length) el = el[0]; // RadioNodeList
    return el || null;
  }

  function syncForms(cart) {
    var limits = readLimits();
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var t = totals(cart);
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      // form.elements also finds fields outside the form that use form="…" (the product page does).
      var idInput = field(form, 'id');
      var note = form.querySelector('[data-ds-limit-note]');
      var info = idInput && limits[String(idInput.value)];
      if (!info) {
        if (note) note.hidden = true;
        continue;
      }
      var r = room(info, String(idInput.value), t);
      var qty = field(form, 'quantity');
      // Quick-add buttons on product cards have no quantity field: they're still capped by
      // the fetch check above, but don't get a note.
      if (!qty) continue;
      var max = String(Math.max(1, r.left));
      if (qty.max !== max) qty.max = max;
      if (r.left > 0 && parseInt(qty.value, 10) > r.left) qty.value = String(r.left);

      if (!note) {
        note = document.createElement('p');
        note.className = 'ds-limit-note';
        note.setAttribute('data-ds-limit-note', '');
        form.appendChild(note);
      }
      var inCart = r.limit - r.left;
      var text = message(r.limit) + (r.left === 0 ? '. You already have ' + inCart + ' in your cart.' : '.');
      if (note.textContent !== text) note.textContent = text;
      if (note.hidden) note.hidden = false;
    }
  }

  var syncTimer = null;
  function scheduleSync(refresh) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      if (!document.querySelector('script[data-ds-order-limit]')) return;
      (refresh || !cartCache ? getCart() : Promise.resolve(cartCache)).then(syncForms).catch(function () {});
    }, 60);
  }

  // Variant changes, quantity edits and newly loaded content (quick view, drawers).
  document.addEventListener('change', function () { scheduleSync(false); }, true);
  document.addEventListener('theme:variant:change', function () { scheduleSync(false); });
  document.addEventListener('theme:cart:change', function () { scheduleSync(true); });
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        if (!rec.addedNodes.length) continue;
        // Ignore our own note updates.
        var target = rec.target.nodeType === 1 ? rec.target : rec.target.parentElement;
        if (target && target.closest && target.closest('[data-ds-limit-note]')) continue;
        if (rec.addedNodes[0].nodeType === 1 && rec.addedNodes[0].hasAttribute('data-ds-limit-note')) continue;
        scheduleSync(false);
        return;
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleSync(true); });
  } else {
    scheduleSync(true);
  }
})();
